import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { Send, Sparkles, Globe, ExternalLink, UserRound, Loader2, ImagePlus, X } from "lucide-react";
import { aiPublicConfig, aiPublicChat } from "@/lib/api";

// Compress an image file to a JPEG data URL (max 1024px, q0.8) to keep payloads small.
const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const max = 1024;
      let { width, height } = img;
      if (width > max || height > max) {
        const r = Math.min(max / width, max / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const md = {
  h1: (p) => <h4 className="font-bold text-slate-900 mt-3 mb-1" {...p} />,
  h2: (p) => <h4 className="font-bold text-slate-900 mt-3 mb-1" {...p} />,
  h3: (p) => <h4 className="font-bold text-slate-900 mt-3 mb-1" {...p} />,
  p: (p) => <p className="mb-2 last:mb-0" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-1 mb-2" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-1 mb-2" {...p} />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
  a: (p) => <a className="text-brand underline" target="_blank" rel="noopener noreferrer" {...p} />,
};

let _c = 0;
const cid = () => `c${++_c}-${Math.random().toString(36).slice(2, 6)}`;

export default function AgentChat({ publicKey }) {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(false);
  const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingImg, setPendingImg] = useState(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    aiPublicConfig(publicKey)
      .then((c) => { setCfg(c); setMessages([{ id: "g", role: "assistant", content: c.welcome_message }]); })
      .catch(() => setError(true));
  }, [publicKey]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { setPendingImg(await compressImage(file)); } catch { /* ignore */ }
  };

  const send = async (text) => {
    const m = (text ?? input).trim();
    const img = pendingImg;
    if ((!m && !img) || busy) return;
    setInput(""); setPendingImg(null);
    setMessages((x) => [...x, { id: cid(), role: "user", content: m, image: img }]);
    setBusy(true);
    try {
      const res = await aiPublicChat(publicKey, sessionId, m || "Please identify and analyze this photo.", img);
      setMessages((x) => [...x, { id: cid(), role: "assistant", content: res.answer, sources: res.sources, handoff: res.handoff }]);
    } catch {
      setMessages((x) => [...x, { id: cid(), role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }]);
    } finally { setBusy(false); }
  };

  if (error) return <div className="text-center text-slate-500 py-20" data-testid="agent-error">Assistant not found or unavailable.</div>;
  if (!cfg) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="flex flex-col h-full bg-white" data-no-translate data-testid="agent-chat">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: cfg.primary_color }}><Sparkles size={17} /></span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-slate-900 leading-tight truncate">{cfg.name}</p>
          <p className="text-[11px] text-slate-400 flex items-center gap-1"><Globe size={10} /> {(cfg.languages || ["English"]).slice(0, 3).join(", ")} · {cfg.vision ? "photo & vision" : "live web"}</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50/50">
        {messages.map((mm) => (
          <div key={mm.id} className={`flex ${mm.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${mm.role === "user" ? "text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`} style={mm.role === "user" ? { background: cfg.primary_color } : {}}>
              {mm.image && <img src={mm.image} alt="upload" className="rounded-lg mb-2 max-h-44 w-auto" data-testid="agent-msg-image" />}
              {mm.role === "user" ? mm.content : <ReactMarkdown components={md}>{mm.content}</ReactMarkdown>}
              {mm.sources?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Sources</p>
                  {mm.sources.slice(0, 4).map((s, i) => (
                    <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] text-brand hover:underline truncate"><ExternalLink size={10} className="shrink-0" /> {s.replace(/^https?:\/\//, "").slice(0, 46)}</a>
                  ))}
                </div>
              )}
              {mm.handoff && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-amber-600"><UserRound size={12} /> Want me to connect you with a person?</div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5"><Loader2 size={15} className="animate-spin text-slate-400" /></div></div>}

        {messages.length <= 1 && cfg.suggestions?.length > 0 && (
          <div className="space-y-2 pt-1">
            {cfg.suggestions.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="agent-suggestion" className="block w-full text-left text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-brand hover:text-brand transition-colors">{s}</button>
            ))}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-100">
        {pendingImg && (
          <div className="relative inline-block mb-2" data-testid="pending-image">
            <img src={pendingImg} alt="to send" className="h-16 w-16 object-cover rounded-lg border border-slate-200" />
            <button onClick={() => setPendingImg(null)} data-testid="remove-image" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-500 flex items-center justify-center shadow-sm"><X size={12} /></button>
          </div>
        )}
        <div className="flex items-center gap-2">
          {cfg.vision && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickImage} className="hidden" data-testid="agent-image-input" />
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="agent-image-btn" title="Upload a photo" className="rounded-lg w-10 h-10 flex items-center justify-center border border-slate-200 text-slate-500 hover:border-brand hover:text-brand disabled:opacity-50"><ImagePlus size={17} /></button>
            </>
          )}
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="agent-input" placeholder={cfg.vision ? "Ask, or snap a food photo…" : "Type your message…"} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
          <button onClick={() => send()} disabled={busy} data-testid="agent-send" className="rounded-lg w-10 h-10 flex items-center justify-center text-white disabled:opacity-50" style={{ background: cfg.primary_color }}><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}
